/**
 * What the worklist warmed, so the viewer's own timing can say whether it helped.
 *
 * The warm-up (lib/cornerstone/studyWarmup) writes here as it starts and
 * finishes each first-paint image; `measureTTFF` in lib/telemetry reads it when
 * the viewport paints, and tags `viewer.perf_ttff` with `warmed` and
 * `warm_lead_ms`. That tag is the whole point of the feature's first release:
 * without it the win is a guess, with it product can read the gain per modality
 * straight out of analytics and decide whether the second tier (streaming the
 * first bundle chunk) is worth its bandwidth.
 *
 * Keyed by series id (the GUID), the same key DicomViewport times TTFF with, so
 * the two line up without either knowing about the other. Dependency-free on
 * purpose: telemetry must not drag Cornerstone in.
 *
 * Alongside the per-series timing record, every image a warm-up actually
 * landed in the cache is indexed by the OBJECT it names (origin + path, with
 * the signature and any frame suffix off). A series warmed for a CT or MR
 * carries a run of slices behind its first paint, and each of those may reach
 * the viewer under a differently-signed URL; the index is how
 * lib/cornerstone/warmedUrl lines an instance row up with the warmed copy.
 */
/** How long a warm record stays answerable; a card opened and forgotten should not tag a read an hour later. */
export const WARM_INFO_TTL_MS = 10 * 60_000;
const entries = new Map();
/** seriesId -> object key -> the imageId the warm-up loaded for that object. */
const warmedObjects = new Map();
/**
 * The object an imageId names, signature and frame suffix off: what two URLs
 * for the same bytes have in common when they were signed in different
 * windows. Null for anything that is not a URL-shaped id.
 */
export function warmObjectKey(imageId) {
    try {
        const url = new URL(imageId.replace(/^wadouri:/, '').replace(/[?&]frame=\d+$/, ''));
        return `${url.origin}${url.pathname}`;
    }
    catch {
        return null;
    }
}
function indexWarmedImage(seriesId, imageId) {
    const key = warmObjectKey(imageId);
    if (!key)
        return;
    let objects = warmedObjects.get(seriesId);
    if (!objects) {
        objects = new Map();
        warmedObjects.set(seriesId, objects);
    }
    objects.set(key, imageId);
}
export function recordWarmStart(seriesId, imageId, now = performance.now()) {
    entries.set(seriesId, { imageId, startedAt: now, completedAt: null });
    warmedObjects.delete(seriesId);
}
export function recordWarmComplete(seriesId, now = performance.now()) {
    const entry = entries.get(seriesId);
    if (!entry)
        return;
    if (entry.completedAt === null)
        entry.completedAt = now;
    indexWarmedImage(seriesId, entry.imageId);
}
/**
 * A further image of an already-started series landed in the cache: a slice
 * behind the first paint. Ignored for a series nobody started warming, so the
 * index can never outlive the record whose clock ages it out.
 */
export function recordWarmedImage(seriesId, imageId) {
    if (!entries.has(seriesId))
        return;
    indexWarmedImage(seriesId, imageId);
}
/**
 * Whether this series' first image was warmed before the viewer asked for it,
 * and how long the warm-up had been running by then. `undefined` when nothing
 * warmed the series (or the record has aged out), so callers can spread the
 * result into a metric payload and let absent fields drop.
 */
export function getWarmInfo(seriesId, now = performance.now()) {
    const entry = getWarmEntry(seriesId, now);
    if (!entry)
        return undefined;
    return { warmed: entry.completedAt !== null, warmLeadMs: Math.round(now - entry.startedAt) };
}
/**
 * The warm record itself, imageId included, so the viewer can line its own
 * first-paint id up with the one that was warmed (lib/cornerstone/warmedUrl).
 * Ages out exactly like {@link getWarmInfo}.
 */
export function getWarmEntry(seriesId, now = performance.now()) {
    const entry = entries.get(seriesId);
    if (!entry)
        return undefined;
    if (now - entry.startedAt > WARM_INFO_TTL_MS) {
        entries.delete(seriesId);
        warmedObjects.delete(seriesId);
        return undefined;
    }
    return entry;
}
/**
 * The imageId the warm-up loaded for `objectKey` (see {@link warmObjectKey})
 * in this series, its first paint or one of the slices behind it, or
 * undefined when nothing landed for that object. Ages out with the series'
 * record, on the same clock as {@link getWarmInfo}.
 */
export function getWarmedImageId(seriesId, objectKey, now = performance.now()) {
    if (!getWarmEntry(seriesId, now))
        return undefined;
    return warmedObjects.get(seriesId)?.get(objectKey);
}
/** Test seam. */
export function resetWarmRegistry() {
    entries.clear();
    warmedObjects.clear();
}
